import { useOne } from "@refinedev/core";
import Input from "components/input"
import { ICreateOrgForm, IPermission, PermissionAction } from "interfaces";
import { useEffect } from "react";
import { UseFormResetField } from "react-hook-form";
import { arrayCompare } from "utills/helpers";

interface PermissionProps {
  resetField: UseFormResetField<ICreateOrgForm>;
  formPermissionValue: IPermission[];
  onChange: (data: IPermission[]) => void;
}

function PermissionSkeleton() {
  return (
    <div className="py-4 sm:px-4 sm:grid sm:grid-cols-6 sm:gap-4 animate-pulse">
      <div className="text-sm sm:col-span-2">
        <div className="h-3 bg-gray-200 rounded-full w-40" />
      </div>
      <div className="grid grid-cols-2 gap-4 mt-6 sm:mt-0 sm:grid-cols-4 sm:col-span-4">
        <div className="w-fit flex items-center">
          <div className="h-3 bg-gray-200 rounded-full w-12"></div>
        </div>
        <div className="w-fit flex items-center">
          <div className="h-3 bg-gray-200 rounded-full w-12"></div>
        </div>
        <div className="w-fit flex items-center">
          <div className="h-3 bg-gray-200 rounded-full w-12"></div>
        </div>
        <div className="w-fit flex items-center">
          <div className="h-3 bg-gray-200 rounded-full w-12"></div>
        </div>
      </div>
    </div>
  )
}

export const Permissions = ({ formPermissionValue = [], onChange, resetField }: PermissionProps) => {

  const {
    data: orgPermissions,
    isLoading: isLoadingOrgPermissions,
    isFetching: isFetchingOrgPermissions,
  } = useOne<IPermission[]>({
    resource: "organization/permissions",
    id: `maximum`,
    
    meta: {
      resource: "data",
    },
  });

  const orgPermissionsData = orgPermissions?.data ?? [];

  const _handleChangeAction = (selectedPermission: IPermission, action: PermissionAction[], isChecked: boolean) => {
    const updatedPermissions = [...formPermissionValue];

    const pIndex = updatedPermissions.findIndex(p => p.resource === selectedPermission.resource && p.app === selectedPermission.app);

    if (pIndex < 0) return;
    
    if (isChecked) {
      const isCheckedViewAction = action.includes(PermissionAction.CREATE) || action.includes(PermissionAction.EDIT);
      updatedPermissions[pIndex].actions = [...new Set([...updatedPermissions[pIndex].actions ,...action, ...isCheckedViewAction ? [PermissionAction.VIEW] : []])];
    } else {
      const filterActions = action.includes(PermissionAction.VIEW) ? [...new Set([...action, ...[PermissionAction.CREATE, PermissionAction.EDIT]])] : action;
      updatedPermissions[pIndex].actions = updatedPermissions[pIndex].actions.filter(actions => !filterActions.includes(actions));
    }

    onChange?.(updatedPermissions);
  }

  useEffect(() => {
    if (orgPermissions?.data) {
      resetField("permissions", {defaultValue: orgPermissions?.data.map(permission => ({
        ...permission,
        actions: permission.actions ?? []
      }))});
    }
  }, [orgPermissions]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bg-transparent divide-y divide-gray-100 sm:bg-magnolia-400 sm:rounded-lg sm:shadow-sm">
      <div className="hidden sm:p-4 sm:grid sm:grid-cols-6 sm:gap-4" hidden>
        <p className="text-sm sm:col-span-2 font-semibold">
          Module menu on Workspace
        </p>
        <p className="text-sm sm:col-span-4 font-semibold">
          Permission
        </p>
      </div>
      {(isLoadingOrgPermissions || isFetchingOrgPermissions) 
      ? (
        <>
          <PermissionSkeleton />
          <PermissionSkeleton />
          <PermissionSkeleton />
        </>
      ) 
      : 
      orgPermissionsData.map((permission: IPermission) => {
        const formPermissionValues = formPermissionValue.find(item => item.resource === permission.resource && item.app === permission.app);
        return (
          <div className="py-4 sm:px-4 sm:grid sm:grid-cols-6 sm:gap-4" key={`${permission.app}:${permission.resource}`}>
            <p className="text-sm sm:col-span-2">{permission.name}</p>
            <div className="grid grid-cols-2 gap-4 mt-6 sm:mt-0 sm:grid-cols-4 sm:col-span-4">
              <div className="w-fit flex items-center">
                <Input
                  type="checkbox"
                  label="All"
                  id={`${permission.app}:${permission.resource}-all`}
                  checked={arrayCompare(permission.actions, formPermissionValues?.actions ?? []) ?? false}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => _handleChangeAction(permission, permission.actions, e.target.checked)}
                />
              </div>
              <div className="w-fit flex items-center">
                {permission.actions.includes(PermissionAction.VIEW) ? (
                  <Input
                    type="checkbox"
                    label="View"
                    id={`${permission.app}:${permission.resource}-view`}
                    checked={formPermissionValues?.actions.includes(PermissionAction.VIEW) ?? false}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => _handleChangeAction(permission, [PermissionAction.VIEW], e.target.checked)}    
                  />
                ) : <></>}
              </div>
              <div className="w-fit flex items-center">
                {permission.actions.includes(PermissionAction.EDIT) ? (
                  <Input
                    type="checkbox"
                    label="Edit"
                    id={`${permission.app}:${permission.resource}-edit`}
                    checked={formPermissionValues?.actions.includes(PermissionAction.EDIT) ?? false}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => _handleChangeAction(permission, [PermissionAction.EDIT], e.target.checked)}    
                  />
                ) : <></>}
              </div>
              <div className="w-fit flex items-center">
                {permission.actions.includes(PermissionAction.CREATE) ? (
                  <Input
                    type="checkbox"
                    label="Create"
                    id={`${permission.app}:${permission.resource}-create`}
                    checked={formPermissionValues?.actions.includes(PermissionAction.CREATE) ?? false}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => _handleChangeAction(permission, [PermissionAction.CREATE], e.target.checked)}    
                  />
                ) : <></>}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}